import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Container, CircularProgress, Alert, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { fetchPublicForm, submitForm } from '../services/api';
import Footer from '../components/Footer';
import FormCard from '../components/FormCard';
import SuccessContent from '../components/SuccessContent';
import { validateFieldsStep, validateSelectionStep } from '../utils/validation';

// ─── Branding resolver ────────────────────────────────────────────────────────
const resolveBranding = (form, organization) => {
  const ev  = form?.event?.branding || {};
  const fb  = form?.branding        || {};
  const pri = organization?.primaryColor || '#1565C0';
  return {
    primaryColor:        fb.primaryColor        || ev.primaryColor        || pri,
    secondaryColor:      fb.secondaryColor       || ev.secondaryColor      || '#FFA726',
    backgroundColor:     fb.backgroundColor      || ev.backgroundColor     || '#EEF2F7',
    cardColor:           fb.cardColor            || ev.cardColor            || '#FFFFFF',
    textColor:           fb.textColor            || ev.textColor            || '#1A2C42',
    fontFamily:          fb.fontFamily           || ev.fontFamily           || 'Inter',
    logoUrl:             fb.logoUrl              || ev.logoUrl              || organization?.logo || null,
    borderRadius:        fb.borderRadius         || ev.borderRadius         || 'medium',
    layout:              fb.layout              || null,
    // New bannerBg system (used by dashboard editor)
    bannerBg:            fb.bannerBg            || null,
    mediaOverlayColor:   fb.mediaOverlayColor   || null,
    mediaOverlayOpacity: fb.mediaOverlayOpacity ?? 0.45,
    heroTextColor:       fb.heroTextColor       || '#ffffff',
    // Legacy mediaUrl fallback
    mediaUrl:            fb.mediaUrl            || null,
    mediaType:           fb.mediaType           || 'image',
  };
};

const resolveSuccessBranding = (successPage, formBranding) => ({
  ...formBranding,
  mediaUrl:            successPage?.mediaUrl            || formBranding.mediaUrl  || null,
  mediaType:           successPage?.mediaType           || formBranding.mediaType || 'image',
  mediaOverlayOpacity: successPage?.mediaOverlayOpacity ?? formBranding.mediaOverlayOpacity ?? 0.45,
});

const submitLabelByType = {
  registration: 'Finish Registration',
  application:  'Submit Application',
  survey:       'Submit Survey',
  feedback:     'Send Feedback',
  waitlist:     'Join Waitlist',
  contact:      'Send Message',
  default:      'Submit',
};

// ─── BannerPanel ─────────────────────────────────────────────────────────────
// Matches the dashboard FormCanvas MediaPanel exactly:
// reads from branding.bannerBg (CSS background string), falls back to legacy mediaUrl.
const BannerPanel = ({ branding, children, sx = {}, contentAlign = 'center' }) => {
  const bannerBg     = branding.bannerBg || {};
  const primaryColor = branding.primaryColor || '#1565C0';

  const isVideo    = bannerBg.type === 'video';
  const videoUrl   = bannerBg.videoUrl || '';
  const bgRaw      = bannerBg.background || '';
  const isImage    = !isVideo && bgRaw.startsWith('url(');
  const isGradient = !isVideo && bgRaw.includes('gradient');

  // Fallback: legacy mediaUrl → build url() string, or gradient from primaryColor
  const fallbackBg = branding.mediaUrl && branding.mediaType !== 'video'
    ? `url('${branding.mediaUrl}') center/cover no-repeat`
    : `linear-gradient(135deg, ${primaryColor} 0%, ${alpha(primaryColor, 0.6)} 100%)`;

  const bgCss = isVideo ? 'transparent' : (bgRaw || fallbackBg);

  // Don't double-apply size/position when bgRaw is already a full shorthand
  const isShorthand = bgRaw.includes('center') || bgRaw.includes('cover') || bgRaw.includes('no-repeat');
  const bgSize = isShorthand ? undefined : (bannerBg.backgroundSize || 'cover');
  const bgPos  = isShorthand ? undefined : (bannerBg.backgroundPosition || 'center');

  // Overlay — only for image and video
  const overlayColor   = branding.mediaOverlayColor  || '#000000';
  const overlayOpacity = branding.mediaOverlayOpacity ?? 0.45;

  const hexToRgba = (hex, op) => {
    const r = parseInt(hex.slice(1, 3), 16) || 0;
    const g = parseInt(hex.slice(3, 5), 16) || 0;
    const b = parseInt(hex.slice(5, 7), 16) || 0;
    return `rgba(${r},${g},${b},${op})`;
  };
  const overlayRgba = overlayColor.startsWith('#')
    ? hexToRgba(overlayColor, overlayOpacity)
    : overlayColor.startsWith('rgba')
      ? overlayColor.replace(/,\s*[\d.]+\)$/, `,${overlayOpacity})`)
      : `rgba(0,0,0,${overlayOpacity})`;

  const showOverlay = overlayOpacity > 0 && (isImage || isVideo || (branding.mediaUrl && branding.mediaType !== 'video'));

  const alignMap = {
    'flex-start': 'flex-start',
    'center':     'center',
    'flex-end':   'flex-end',
  };
  const justifyContent = alignMap[contentAlign] || 'center';

  return (
    <Box sx={{ position: 'relative', overflow: 'hidden', ...sx }}>
      {/* Background */}
      {!isVideo && (
        <Box sx={{
          position: 'absolute', inset: 0,
          background: bgCss,
          ...(bgSize ? { backgroundSize: bgSize } : {}),
          ...(bgPos  ? { backgroundPosition: bgPos } : {}),
        }} />
      )}
      {/* Video */}
      {isVideo && videoUrl && (
        <video autoPlay muted loop playsInline
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}>
          <source src={videoUrl} />
        </video>
      )}
      {/* Legacy video fallback */}
      {!isVideo && !bgRaw && branding.mediaUrl && branding.mediaType === 'video' && (
        <video autoPlay muted loop playsInline
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}>
          <source src={branding.mediaUrl} />
        </video>
      )}
      {/* Overlay */}
      {showOverlay && (
        <Box sx={{ position: 'absolute', inset: 0, background: overlayRgba }} />
      )}
      {/* Content */}
      <Box sx={{ position: 'relative', zIndex: 1, height: '100%', display: 'flex', flexDirection: 'column', justifyContent, p: { xs: 3, md: 5 } }}>
        {children}
      </Box>
    </Box>
  );
};

// ─── HeroBanner ──────────────────────────────────────────────────────────────
// Renders tagline / title / subtitle using the same sizes as the dashboard canvas.
const HeroBanner = ({ form, branding, align = 'center' }) => {
  const textColor = branding.heroTextColor || '#ffffff';
  const tagline  = form?.headerTitle    || null;
  const title    = form?.headerSubtitle || form?.name || null;
  const subtitle = form?.description    || null;

  if (!tagline && !title && !subtitle) return null;

  return (
    <Box sx={{ textAlign: align }}>
      {tagline && (
        <Typography sx={{ fontSize: '0.65rem', fontWeight: 800, letterSpacing: '0.2em', textTransform: 'uppercase', color: alpha(textColor, 0.85), mb: 0.75, display: 'block' }}>
          {tagline}
        </Typography>
      )}
      {title && (
        <Typography sx={{ fontSize: { xs: '1.5rem', md: '2rem' }, fontWeight: 800, color: textColor, lineHeight: 1.1, textShadow: '0 1px 10px rgba(0,0,0,0.3)', mb: subtitle ? 1 : 0 }}>
          {title}
        </Typography>
      )}
      {subtitle && (
        <Typography sx={{ fontSize: '1rem', color: alpha(textColor, 0.85), lineHeight: 1.5, maxWidth: 560, ...(align === 'center' ? { mx: 'auto' } : {}) }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// LAYOUT COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════════

const SplitFlushLayout = ({ side, branding, form, content }) => {
  const contentAlign = branding.bannerBg?.contentAlign || 'flex-end';
  const mediaPanel = (
    <BannerPanel branding={branding} contentAlign={contentAlign}
      sx={{ flex: '0 0 30%', minHeight: { xs: 280, md: '100%' } }}>
      <HeroBanner form={form} branding={branding} align={side === 'left' ? 'left' : 'right'} />
    </BannerPanel>
  );
  const contentColumn = (
    <Box sx={{ flex: '0 0 70%', display: 'flex', flexDirection: 'column', minHeight: 0, bgcolor: 'background.paper', alignItems: 'center' }}>
      <Box sx={{ width: '100%', maxWidth: 900, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        {content}
      </Box>
    </Box>
  );
  return (
    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, flex: 1, minHeight: 0, overflow: 'hidden' }}>
      {side === 'left' ? <>{mediaPanel}{contentColumn}</> : <>{contentColumn}{mediaPanel}</>}
    </Box>
  );
};

const SplitCardLayout = ({ side, branding, form, content }) => {
  const contentAlign = branding.bannerBg?.contentAlign || 'flex-end';
  const mediaPanel = (
    <BannerPanel branding={branding} contentAlign={contentAlign}
      sx={{ flex: '0 0 40%', borderRadius: 0, minHeight: { xs: 260, md: 0 } }}>
      <HeroBanner form={form} branding={branding} align={side === 'left' ? 'left' : 'right'} />
    </BannerPanel>
  );
  const contentColumn = (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {content}
    </Box>
  );
  return (
    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: 2, md: 5 }, bgcolor: 'background.default' }}>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, width: '100%', maxWidth: 1100, height: { md: '70vh' }, maxHeight: { md: 780 }, borderRadius: 4, overflow: 'hidden', boxShadow: '0 8px 48px rgba(0,0,0,0.14)', border: '1px solid', borderColor: 'divider' }}>
        {side === 'left' ? <>{mediaPanel}{contentColumn}</> : <>{contentColumn}{mediaPanel}</>}
      </Box>
    </Box>
  );
};

const TopBannerLayout = ({ branding, form, content }) => {
  const contentAlign = branding.bannerBg?.contentAlign || 'center';
  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
      <BannerPanel branding={branding} contentAlign={contentAlign} sx={{ height: { xs: 260, md: 340 }, flexShrink: 0 }}>
        <HeroBanner form={form} branding={branding} align="center" />
      </BannerPanel>
      <Container maxWidth={false} sx={{ flex: 1, pb: 8, pt: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', maxWidth: 900, mx: 'auto', width: '100%' }}>
        {content}
      </Container>
    </Box>
  );
};

const BgLayout = ({ branding, form, content }) => {
  const contentAlign = branding.bannerBg?.contentAlign || 'center';
  return (
    <BannerPanel branding={branding} contentAlign="flex-start" sx={{ flex: 1 }}>
      <Box sx={{ width: '100%', maxWidth: 900, mx: 'auto', display: 'flex', flexDirection: 'column', gap: 3 }}>
        <HeroBanner form={form} branding={branding} align="center" />
        {content}
      </Box>
    </BannerPanel>
  );
};

// Single-column: just the form card centered, no banner
const SingleColumnLayout = ({ branding, content }) => (
  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', bgcolor: branding.backgroundColor || '#EEF2F7' }}>
    <Container maxWidth={false} sx={{ flex: 1, pb: 8, pt: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', maxWidth: 720, mx: 'auto', width: '100%' }}>
      {content}
    </Container>
  </Box>
);

const Layout = ({ layout, branding, form, content }) => {
  switch (layout) {
    case 'image-left':  return <SplitFlushLayout side="left"  branding={branding} form={form} content={content} />;
    case 'image-right': return <SplitFlushLayout side="right" branding={branding} form={form} content={content} />;
    case 'card-left':   return <SplitCardLayout  side="left"  branding={branding} form={form} content={content} />;
    case 'card-right':  return <SplitCardLayout  side="right" branding={branding} form={form} content={content} />;
    case 'image-bg':    return <BgLayout         branding={branding} form={form} content={content} />;
    case 'image-top':   return <TopBannerLayout  branding={branding} form={form} content={content} />;
    default:            return <SingleColumnLayout branding={branding} content={content} />;
  }
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════════════
const FormPage = ({ preloadedForm, preloadedOrganization } = {}) => {
  const params   = useParams();
  const formSlug = preloadedForm ? null : (params.formSlug || params['*']);

  const [loading,      setLoading]      = useState(!preloadedForm);
  const [submitting,   setSubmitting]   = useState(false);
  const [error,        setError]        = useState(null);
  const [form,         setForm]         = useState(preloadedForm || null);
  const [organization, setOrganization] = useState(preloadedOrganization || null);
  const [branding,     setBranding]     = useState(
    preloadedForm ? resolveBranding(preloadedForm, preloadedOrganization) : {}
  );

  const [activeStep,      setActiveStep]      = useState(0);
  const [fieldData,       setFieldData]       = useState({});
  const [selectionData,   setSelectionData]   = useState({});
  const [fieldErrors,     setFieldErrors]     = useState({});
  const [selectionErrors, setSelectionErrors] = useState({});
  const [submitResult,    setSubmitResult]    = useState(null);

  const formRef = useRef(null);
  const scrollToForm = useCallback(() => {
    if (formRef.current) formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    else window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (preloadedForm) return;
    const load = async () => {
      try {
        setLoading(true);
        const res = await fetchPublicForm(null, formSlug);
        const f   = res.data.data;
        const o   = res.data.organization;
        setForm(f);
        setOrganization(o);
        setBranding(resolveBranding(f, o));
      } catch (err) {
        setError(err.response?.data?.message || 'Form not found or no longer available.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [formSlug, preloadedForm]);

  // Update favicon whenever the form's event branding changes
  useEffect(() => {
    const logoUrl = form?.event?.branding?.logoUrl;
    if (logoUrl) {
      let link = document.querySelector("link[rel='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = logoUrl;
    }
    return () => {
      const link = document.querySelector("link[rel='icon']");
      if (link) link.href = '/favicon.ico';
    };
  }, [form?.event?.branding?.logoUrl]);

  // Update document title whenever the form changes
  useEffect(() => {
    if (form?.name && form?.event?.name) {
      document.title = `${form.name} | ${form.event.name}`;
    } else if (form?.name) {
      document.title = form.name;
    }
    return () => { document.title = ''; };
  }, [form?.name, form?.event?.name]);

  const steps       = form ? [...(form.steps || [])].sort((a, b) => a.order - b.order) : [];
  const currentStep = steps[activeStep];
  const isLastStep  = activeStep === steps.length - 1;

  const handleFieldChange     = useCallback((id, val) => { setFieldData(p => ({ ...p, [id]: val })); setFieldErrors(p => ({ ...p, [id]: null })); }, []);
  const handleSelectionChange = useCallback((id, val) => { setSelectionData(p => ({ ...p, [id]: val })); setSelectionErrors(p => ({ ...p, [id]: null })); }, []);

  const validateCurrent = () => {
    if (!currentStep) return true;
    if (currentStep.type === 'fields') {
      const { isValid, errors } = validateFieldsStep(currentStep, fieldData);
      if (!isValid) { setFieldErrors(errors); return false; }
    }
    if (currentStep.type === 'selection') {
      const { isValid, errors } = validateSelectionStep(currentStep, selectionData);
      if (!isValid) { setSelectionErrors(errors); return false; }
    }
    return true;
  };

  const handleNext   = () => { if (!validateCurrent()) return; setActiveStep(s => s + 1); scrollToForm(); };
  const handleBack   = () => { setActiveStep(s => s - 1); scrollToForm(); };

  const handleSubmit = async () => {
    if (!validateCurrent()) return;
    try {
      setSubmitting(true);
      const fd = new FormData();
      fd.append('formId', form._id);
      fd.append('selectionData', JSON.stringify(selectionData));
      const plain = {};
      Object.entries(fieldData).forEach(([k, v]) => {
        if (v instanceof File) fd.append(k, v);
        else plain[k] = v;
      });
      fd.append('fieldData', JSON.stringify(plain));
      const res = await submitForm(fd);
      setSubmitResult(res.data.data);
      scrollToForm();
    } catch (err) {
      setError(err.response?.data?.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress />
    </Box>
  );

  if (error && !form) return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
      <Alert severity="error" sx={{ maxWidth: 480 }}>{error}</Alert>
    </Box>
  );

  const layout          = branding.layout || 'image-top';
  const successBranding = resolveSuccessBranding(form?.successPage, branding);
  const activeLayout    = submitResult ? successBranding.layout : layout;
  const activeBranding  = submitResult ? successBranding : branding;
  const isSplitLayout   = ['image-left', 'image-right', 'card-left', 'card-right'].includes(activeLayout);

  const formCardVariant = ['image-left', 'image-right'].includes(layout)
    ? 'flush'
    : ['card-left', 'card-right'].includes(layout)
      ? 'incard'
      : 'card';

  const formContent = (
    <FormCard
      steps={steps}
      activeStep={activeStep}
      currentStep={currentStep}
      isLastStep={isLastStep}
      fieldData={fieldData}
      selectionData={selectionData}
      fieldErrors={fieldErrors}
      selectionErrors={selectionErrors}
      error={error}
      submitting={submitting}
      onFieldChange={handleFieldChange}
      onSelectionChange={handleSelectionChange}
      onNext={handleNext}
      onBack={handleBack}
      onSubmit={handleSubmit}
      onClearError={() => setError(null)}
      submitLabel={submitLabelByType[form?.formType] || submitLabelByType.default}
      variant={formCardVariant}
    />
  );

  const successContentNode = (
    <SuccessContent result={submitResult} variant={formCardVariant} />
  );

  return (
    <Box sx={{ height: isSplitLayout ? '100vh' : undefined, minHeight: isSplitLayout ? undefined : '100vh', overflow: isSplitLayout ? 'hidden' : undefined, bgcolor: 'background.default', display: 'flex', flexDirection: 'column' }}>
      <Box ref={formRef} sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Layout
          layout={activeLayout}
          branding={activeBranding}
          form={form}
          content={submitResult ? successContentNode : formContent}
        />
      </Box>
      {!isSplitLayout && <Footer branding={branding} />}
    </Box>
  );
};

export default FormPage;